import { useCallback } from 'react'
import { useSearchParams } from 'hooks/useSearchParams'
import type { AuditRunStatus } from '../api/enrollment-overlap.types'
import { buildPagingPatch, parsePaging, parseStatus } from '../utils/paging'

interface PagingQuery {
  page?: string
  pageSize?: string
  status?: string
}

/**
 * `page`, `pageSize` and (optionally) `status` live in the URL query string so
 * every view is shareable. Invalid values fall back to valid ones.
 */
export function usePagingParams() {
  const { params, setParams } = useSearchParams<PagingQuery>()

  const { page, pageSize } = parsePaging(params)
  const status = parseStatus(params.status)

  const setPaging = useCallback(
    (nextPage: number, nextPageSize: number) => {
      // Changing the page size would land on a different slice of the data,
      // so it restarts at page 1.
      const resolvedPage = nextPageSize === pageSize ? nextPage : 1
      setParams(buildPagingPatch(resolvedPage, nextPageSize))
    },
    [pageSize, setParams]
  )

  const setStatus = useCallback(
    (nextStatus: AuditRunStatus | undefined) => {
      // A new filter restarts at page 1.
      setParams({ status: nextStatus, page: undefined })
    },
    [setParams]
  )

  const resetToFirstPage = useCallback(() => {
    setParams({ page: undefined }, { replace: true })
  }, [setParams])

  return { page, pageSize, status, setPaging, setStatus, resetToFirstPage }
}
